<template>
  <component
    :is="tag"
    :class="[
      'button',
      {
        [`button--${theme}`]: theme,
        'button--fullWidth': fullWidth,
        'button--inlineTextButton': isInlineTextButton,
        'button--smallHeight': smallHeight,
        'button--mobileSmallHeight': mobileSmallHeight,
        'button--basketButton': isBasketButton,
        'button--smallBasketButton': isSmallBasketButton,
        'button--smallCartDropdown': isSmallDropdownButton,
      },
    ]"
    :disabled="disabled"
    :to="to"
    :href="href"
    :type="tag === 'button' ? 'button' : ''"
    @click="onClick"
  >
    <template v-if="isLoading">
      <svg
        class="button__loader"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        viewBox="0 0 100 100"
        enable-background="new 0 0 0 0"
        xml:space="preserve"
      >
        <path
          d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
        >
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            dur="1s"
            from="0 50 50"
            to="360 50 50"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    </template>
    <template v-else>
      <span v-if="hasHtml" class="button__inner">
        <slot />
      </span>
      <template v-else>
        {{ text }}
      </template>
    </template>
  </component>
</template>

<script>
export default {
  name: 'VButton',
  props: {
    text: {
      type: String,
      default: '',
    },
    to: {
      type: String,
      default: undefined,
    },
    href: {
      type: String,
      default: undefined,
    },
    theme: {
      type: String,
      default: null,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    smallHeight: {
      type: Boolean,
      default: false,
    },
    isInlineTextButton: {
      type: Boolean,
      default: false,
    },
    mobileSmallHeight: {
      type: Boolean,
      default: false,
    },
    hasHtml: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isBasketButton: {
      type: Boolean,
      default: false,
    },
    isSmallBasketButton: {
      type: Boolean,
      default: false,
    },
    isSmallDropdownButton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tag() {
      if (this.to) {
        return 'nuxt-link';
      } else if (this.href) {
        return 'a';
      } else {
        return 'button';
      }
    },
  },
  methods: {
    onClick(e) {
      this.$emit('click', e);
    },
  },
};
</script>

<style lang="scss">
.button {
  $root: &;
  height: 5.4rem;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 3rem;
  font-size: 1.4rem;
  line-height: 1.8rem;
  font-weight: 700;
  border-radius: 4px;
  white-space: nowrap;
  transition: background-color $tr, box-shadow $tr;

  &__inner {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon {
    font-size: 2rem;
    padding-right: 1rem;
  }

  &__loader {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &--white {
    background-color: $cWhite;
    color: $cMain;
    box-shadow: 0 3px 6px rgba($cWhite, 0.1);

    &:hover {
      box-shadow: 0 6px 12px rgba($cWhite, 0.15);
    }

    #{$root}__loader {
      width: 50px;

      path {
        fill: $cSec;
      }
    }
  }

  &--whiteBorder {
    background-color: $cWhite;
    color: $cMain;
    border: 1px solid $cAddLight;
    transition: border $tr;

    &:hover {
      border: 1px solid $cMain;
    }

    #{$root}__loader {
      width: 50px;

      path {
        fill: $cSec;
      }
    }
  }

  &--purple {
    background-color: $cMain;
    color: $cWhite;
    box-shadow: 0 3px 6px rgba(15, 2, 34, 0.1);

    &:hover:not(:disabled) {
      background-color: $cMainHover;
      box-shadow: 0 6px 12px rgba(15, 2, 34, 0.2);
    }

    #{$root}__loader {
      width: 50px;

      path {
        fill: $cWhite;
      }
    }
  }

  &--lightPurple {
    background-color: $cLightMain;
    color: $cMain;

    &:hover:not(:disabled) {
      background-color: $cLightMainHover;
    }

    #{$root}__loader {
      width: 50px;

      path {
        fill: $cWhite;
      }
    }
  }

  &--orange {
    background-color: $cSec;
    color: $cWhite;
    box-shadow: 0 3px 6px rgba(15, 2, 34, 0.1);

    &:hover:not(:disabled) {
      background-color: $cSecHover;
    }

    #{$root}__loader {
      width: 50px;

      path {
        fill: $cWhite;
      }
    }
  }

  &--fullWidth {
    width: 100%;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  &--smallHeight {
    height: 4.8rem;
  }

  &--mobileSmallHeight {
    @include rwd('tablet') {
      height: 4.8rem;
      margin-top: 2.2rem;
    }
  }

  &--lightOutline {
    border: 1px solid $cAddLight;
    height: 4rem;
    font-size: 1.2rem;
    line-height: 1.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    color: $cMain;

    #{$root}__icon {
      color: $cAddNeutral;
    }

    &:hover:not(:disabled) {
      box-shadow: 0 3px 6px rgba(15, 2, 34, 0.05);
    }
  }

  &--outlineLightMain {
    border: 1px solid rgba($cWhite, 0.24);
    color: $cWhite;
    transition: border $tr;

    &:hover {
      border: 1px solid rgba($cWhite, 0.5);
    }
  }

  &--basketButton {
    height: 3.8rem;
    width: 3.8rem;
    flex: 0 0 3.8rem;
    padding: 0 0 0.3rem 0;
    font-weight: 700;
    font-size: 2.2rem;
  }

  &--smallBasketButton {
    height: 3.2rem;
    width: 3.2rem;
    flex: 0 0 3.2rem;
    padding: 0 0 0.3rem 0;
    font-weight: 700;
    font-size: 2rem;
  }

  &--smallCartDropdown {
    height: 2.2rem;
    width: 2.2rem;
    flex: 0 0 2.2rem;
    padding: 0 0 0.3rem 0;
    font-weight: 700;
    font-size: 1.6rem;
  }

  &--inlineTextButton {
    padding: 0.2rem 1rem;
    height: 3.2rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
